import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentApi } from "../../../modules/agent/api";
import {
  AgentEducationFilterPageRequest,
  CreateUpdateAgentCompletedEducation,
  CreateUpdateAgentEducation,
  UpdateAgentCompletedEducationDetail,
} from "../../../modules/agent/types";
import { EnumerationType } from "../../../modules/enumerations/enums";
import type { UUID } from "../../../typings/global";
import { EntityObject, PageRequest, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const AGENT_EDUCATION_QUERY_KEYS = {
  filterAgentEducations: (filter?: AgentEducationFilterPageRequest) =>
    filter ? ["filterAgentEducations", filter] : ["filterAgentEducations"],
  createAgentEducation: () => ["createAgentEducation"],
  updateAgentEducation: () => ["updateAgentEducation"],
  deleteAgentEducation: () => ["deleteAgentEducation"],
  getAgentCompletedEducation: (request?: EntityObject<PageRequest>) =>
    request ? ["getAgentCompletedEducation", request] : ["getAgentCompletedEducation"],
  createAgentCompletedEducation: () => ["createAgentCompletedEducation"],
  updateAgentCompletedEducation: () => ["updateAgentCompletedEducation"],
  deleteAgentCompletedEducation: () => ["deleteAgentCompletedEducation"],
  getAgentCompletedEducationDetail: (id: UUID) => ["getAgentCompletedEducationDetail", id],
  updateAgentCompletedEducationDetail: () => ["updateAgentCompletedEducationDetail"],
};

export const useFilterAgentEducations = (payload: AgentEducationFilterPageRequest) => {
  return useQuery({
    queryKey: AGENT_EDUCATION_QUERY_KEYS.filterAgentEducations(payload),
    queryFn: async () => {
      const { data } = await agentApi.filterAgentEducations(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateAgentEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.createAgentEducation(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateAgentEducation) => {
      const { data } = await agentApi.createAgentEducation(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_EDUCATIONS);
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.filterAgentEducations() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.updateAgentEducation(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentEducation>) => {
      const { data } = await agentApi.updateAgentEducation(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_EDUCATIONS);
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.filterAgentEducations() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.deleteAgentEducation(),
    mutationFn: (id: UUID) => {
      return agentApi.deleteAgentEducation({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AGENT_EDUCATIONS);
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.filterAgentEducations() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetAgentCompletedEducations = (payload: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducation(payload),
    queryFn: async () => {
      const { data } = await agentApi.getAgentCompletedEducations(payload);

      return data;
    },
  });
};

export const useCreateAgentCompletedEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.createAgentCompletedEducation(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAgentCompletedEducation>) => {
      const { data } = await agentApi.createAgentCompletedEducation(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducation() });
      queryClient.invalidateQueries({
        queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducationDetail(mutatedPayload.id),
      });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAgentCompletedEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.updateAgentCompletedEducation(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateAgentCompletedEducation>) => {
      const { data } = await agentApi.updateAgentCompletedEducation(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducation() });
      queryClient.invalidateQueries({
        queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducationDetail(mutatedPayload.id1),
      });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAgentCompletedEducation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.deleteAgentCompletedEducation(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return agentApi.deleteAgentCompletedEducation(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducation() });
      queryClient.invalidateQueries({
        queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducationDetail(mutatedPayload.id1),
      });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetAgentCompletedEducationDetail = (id: UUID) => {
  return useQuery({
    queryKey: AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducationDetail(id),
    queryFn: async () => {
      const { data } = await agentApi.getAgentCompletedEducationDetail({ id });

      return data;
    },
  });
};

export const useUpdateAgentCompletedEducationDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AGENT_EDUCATION_QUERY_KEYS.updateAgentCompletedEducationDetail(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateAgentCompletedEducationDetail>) => {
      const { data } = await agentApi.updateAgentCompletedEducationDetail(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(AGENT_EDUCATION_QUERY_KEYS.getAgentCompletedEducationDetail(mutatedPayload.id), data);
    },
  });
};
